import { useEffect, useReducer } from 'react';
import { fetchWorkspacePolicyCheck } from '../../shared/agent-consent/domain/policyClient';
import { useAnalytics } from '../../../Analytics/AnalyticsProvider';
import {
  getToolsetNames,
  parseMcpPolicyData,
} from '../../MCP/domain/policyParse';
import {
  getPolicyRequestKey,
  getPolicyStateForRequest,
  MCP_POLICY_ERROR_KIND,
  policyReducer,
  type PolicyState,
} from './policyState';

import type { BitbucketWorkspace } from '#definitions/consent-info';
import type { ConsentToken } from '#definitions/context';

export type UseBitbucketPolicyParams = {
  token: ConsentToken;
  isEnabled: boolean;
  /** The workspace to check. Bitbucket consent grants a single workspace. */
  selectedBitbucketWorkspace: BitbucketWorkspace | null;
  onToolsetsLoaded: (toolsetNames: Set<string>) => void;
};

export function useBitbucketPolicy({
  token,
  isEnabled,
  selectedBitbucketWorkspace,
  onToolsetsLoaded,
}: UseBitbucketPolicyParams): PolicyState {
  const analyticsClient = useAnalytics();
  const [policyState, dispatch] = useReducer(policyReducer, {
    status: 'idle',
  });

  useEffect(() => {
    if (token.serviceType !== 'MCP') return;

    if (!isEnabled || !selectedBitbucketWorkspace) {
      dispatch({ type: 'RESET' });
      return;
    }

    const requestKey = getPolicyRequestKey(token.raw, [
      selectedBitbucketWorkspace.ari,
    ]);
    const ac = new AbortController();
    dispatch({ type: 'LOADING', requestKey });

    void (async () => {
      try {
        const result = await fetchWorkspacePolicyCheck(
          {
            contextJwt: token.raw,
            selectedWorkspace: selectedBitbucketWorkspace.ari,
          },
          ac.signal,
        );

        if (ac.signal.aborted) return;

        if (!result.ok) {
          dispatch({
            type: 'ERROR',
            requestKey,
            errorMessage: result.errorMessage,
            errorKind: MCP_POLICY_ERROR_KIND.policyFetchFailed,
          });
          analyticsClient.pushTrackEvent({
            actionSubject: 'mcpPolicyCheck',
            action: 'failed',
            source: 'oauthConsentScreen',
            attributes: { reason: MCP_POLICY_ERROR_KIND.policyFetchFailed },
          });
          return;
        }

        if (!result.data.redirectAllowed) {
          dispatch({
            type: 'ERROR',
            requestKey,
            errorMessage: null,
            errorKind: MCP_POLICY_ERROR_KIND.redirectNotAllowed,
          });
          analyticsClient.pushTrackEvent({
            actionSubject: 'mcpPolicyCheck',
            action: 'failed',
            source: 'oauthConsentScreen',
            attributes: { reason: MCP_POLICY_ERROR_KIND.redirectNotAllowed },
          });
          return;
        }

        const byIntent = parseMcpPolicyData(result.data).byIntent;
        dispatch({
          type: 'SUCCESS',
          requestKey,
          byIntent,
          // Cross-org scope removal is a Trello-only concept.
          crossOrgScopesRemoved: false,
        });
        onToolsetsLoaded(getToolsetNames(byIntent));
      } catch (e) {
        if (
          (e instanceof DOMException && e.name === 'AbortError') ||
          ac.signal.aborted
        )
          return;
        dispatch({
          type: 'ERROR',
          requestKey,
          errorMessage: e instanceof Error ? e.message : 'Unexpected error',
          errorKind: MCP_POLICY_ERROR_KIND.unexpected,
        });
      }
    })();

    return () => {
      ac.abort();
    };
  }, [
    token,
    isEnabled,
    selectedBitbucketWorkspace,
    analyticsClient,
    onToolsetsLoaded,
  ]);

  return getPolicyStateForRequest(
    policyState,
    isEnabled && selectedBitbucketWorkspace
      ? getPolicyRequestKey(token.raw, [selectedBitbucketWorkspace.ari])
      : null,
  );
}
